import { useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

export function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const query = useMemo(() => {
    const q: Record<string, string | string[]> = { ...params }
    searchParams.forEach((value, key) => {
      if (q[key]) {
        if (Array.isArray(q[key])) {
          ;(q[key] as string[]).push(value)
        } else {
          q[key] = [q[key] as string, value]
        }
      } else {
        q[key] = value
      }
    })
    return q
  }, [params, searchParams])

  const router = useMemo(() => {
    return {
      pathname: location.pathname,
      route: location.pathname,
      asPath: location.pathname + location.search + location.hash,
      query,
      isReady: true,
      basePath: '',
      locale: 'en',
      locales: ['en', 'id'],
      defaultLocale: 'en',
      push: (url: any, as?: any, options?: any) => {
        const dest =
          typeof url === 'object'
            ? (url.pathname || '/') + (url.query ? `?${new URLSearchParams(url.query).toString()}` : '')
            : url
        navigate(dest, { replace: false })
        return Promise.resolve(true)
      },
      replace: (url: any, as?: any, options?: any) => {
        const dest =
          typeof url === 'object'
            ? (url.pathname || '/') + (url.query ? `?${new URLSearchParams(url.query).toString()}` : '')
            : url
        navigate(dest, { replace: true })
        return Promise.resolve(true)
      },
      reload: () => {
        window.location.reload()
      },
      back: () => {
        navigate(-1)
      },
      forward: () => {
        navigate(1)
      },
      prefetch: () => Promise.resolve(),
      beforePopState: () => {},
      events: {
        on: () => {},
        off: () => {},
        emit: () => {}
      }
    }
  }, [location, query, navigate])

  return router
}

export type NextRouter = ReturnType<typeof useRouter>
export default { useRouter }
